import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export type PlannedHoursBaselineSource = 'quote_sent' | 'marked_planned';

export interface WorkOrderPlanningService {
  id: string;
  title: string;
  plannedHours: number | null;
  baselineHours: number | null;
  baselineAt: string | null;
  baselineSource: PlannedHoursBaselineSource | null;
  actualHours: number;
}

export interface WorkOrderPlanning {
  services: WorkOrderPlanningService[];
  totals: {
    plannedHours: number | null;
    baselineHours: number | null;
    actualHours: number;
  };
}

export function workOrderPlanningQueryKey(workOrderId: string) {
  return [...queryKeys.workOrders.detail(workOrderId), 'planning'] as const;
}

export function useWorkOrderPlanning(workOrderId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: workOrderPlanningQueryKey(workOrderId),
    queryFn: () => api.get<WorkOrderPlanning>(`/work-orders/${workOrderId}/planning`),
    enabled: workOrderId.length > 0,
  });

  const markPlanned = useMutation({
    mutationFn: () => api.post<WorkOrderPlanning>(`/work-orders/${workOrderId}/mark-planned`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
      await queryClient.invalidateQueries({ queryKey: workOrderPlanningQueryKey(workOrderId) });
    },
  });

  return { query, markPlanned };
}
